import { AxiosInstance } from 'axios';
import { KeycloakInstance } from 'keycloak-js';

/**
 * Setup axios interceptors to use Keycloak token
 *
 * @param axiosInstance - Axios instance to configure
 * @param keycloak - Keycloak instance
 */
export const setupAxiosKeycloak = (
  axiosInstance: AxiosInstance,
  keycloak: KeycloakInstance | null
) => {
  // Request interceptor
  axiosInstance.interceptors.request.use(
    async (config) => {
      if (keycloak && keycloak.authenticated) {
        // Update token if needed (will refresh if expired)
        try {
          await keycloak.updateToken(30); // Refresh if expires in 30 seconds

          // Add token to request
          if (keycloak.token) {
            config.headers.Authorization = `Bearer ${keycloak.token}`;
          }
        } catch (error) {
          console.error('Failed to update Keycloak token:', error);
          // Token refresh failed, user needs to login again
          keycloak.login();
        }
      } else {
        // Fallback to localStorage token (for backwards compatibility)
        const token = localStorage.getItem('auth_token');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
      }

      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );

  // Response interceptor
  axiosInstance.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (error.response?.status === 401) {
        // Unauthorized - token might be expired
        if (keycloak && keycloak.authenticated) {
          try {
            // Try to refresh token
            await keycloak.updateToken(-1); // Force refresh

            // Retry the original request
            if (keycloak.token && error.config) {
              error.config.headers.Authorization = `Bearer ${keycloak.token}`;
              return axiosInstance.request(error.config);
            }
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);
            // Refresh failed, redirect to login
            keycloak.login();
          }
        } else {
          // Not authenticated or fallback auth failed
          localStorage.removeItem('auth_token');
          window.location.href = '/login';
        }
      }
      return Promise.reject(error);
    }
  );
};
